import {DocumentAttachmentType, DocumentAttachment} from '@/interfaces/document';
import {downloadAttachment} from '@/repositories/beneva-api/endpoints/documents';

interface ExtractedDetails {
  transit: string;
  bank: string;
  account: string;
}

const processPDF = async (attachment: DocumentAttachment): Promise<Blob> => {
  const PDFJS = await import('pdfjs-dist');
  PDFJS.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

  const pdf = await PDFJS.getDocument({data: Uint8Array.from(atob(attachment.content), c => c.charCodeAt(0))}).promise;
  const page = await pdf.getPage(1);
  const scale = 2;
  const viewport = page.getViewport({scale});
  const source = document.createElement('canvas');
  const context = source.getContext('2d') as CanvasRenderingContext2D;

  source.height = viewport.viewBox[2] * scale;
  source.width = viewport.viewBox[3] * scale;

  await page.render({canvas: source, canvasContext: context, viewport}).promise;
  context.scale(-1, 1);

  return new Promise((resolve, reject) => {
    const canvas = toCanvasOCRElement(source);
    canvas.convertToBlob().then(resolve).catch(reject);
  });
};

const processImage = async (attachment: DocumentAttachment): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = toCanvasOCRElement(image);
      canvas?.convertToBlob().then(resolve).catch(reject);
    };

    image.onerror = reject;
    image.src = `data:image/${attachment.type};base64,${attachment.content}`;
  });
};

// Convert a Canvas or an Image element into an OCR ready canvas to be processed by tesseract.js
// The canvas only includes the 25% bottom of the source.
const toCanvasOCRElement = (source: HTMLCanvasElement | HTMLImageElement): OffscreenCanvas => {
  const croppedHeight = source.height * 0.25;
  const canvas = new OffscreenCanvas(source.width, croppedHeight);

  const context = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
  context.drawImage(
    source,
    0,
    source.height - croppedHeight,
    source.width,
    croppedHeight,
    0,
    0,
    source.width,
    croppedHeight,
  );

  return canvas;
};

function attachmentToBlob(attachment: DocumentAttachment): Promise<Blob> {
  if (attachment.type === DocumentAttachmentType.image) return processImage(attachment);
  if (attachment.type === DocumentAttachmentType.pdf) return processPDF(attachment);

  return new Promise(resolve => resolve(new Blob()));
}

export const extractDetailsFromAttachment = async (
  documentId: string,
  attachmentId: string,
): Promise<ExtractedDetails> => {
  // Check for mock function (used in Cypress tests)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mockFn = (window as any).__mockExtractDetailsFromAttachment;
  if (mockFn && typeof mockFn === 'function') {
    return mockFn(documentId, attachmentId);
  }

  const {createWorker, PSM} = await import('tesseract.js');
  const result: ExtractedDetails = {
    transit: '',
    bank: '',
    account: '',
  };

  const {data: attachment} = await downloadAttachment(documentId, attachmentId);

  const blob = await attachmentToBlob(attachment);
  const worker = await createWorker('mirc', 0, {langPath: '/ocr', gzip: false});
  await worker.setParameters({tessedit_pageseg_mode: PSM.AUTO});
  const {
    data: {text: rawText},
  } = await worker.recognize(blob);
  const text = rawText.replaceAll(/[\n\s]/g, '');

  const matches = {
    transit: text.match(/[\\dbcd]*a(\d{5})d/),
    bank: text.match(/.*d(\d{3})a/),
    account: text.match(/[\\dbcd]*a([\dd]+)c/),
  };

  if (matches.transit?.[1]) result.transit = matches.transit[1];
  if (matches.bank?.[1]) result.bank = matches.bank[1];
  if (matches.account?.[1]) result.account = matches.account[1].replace(/d/g, '');

  return result;
};
